import React, { useEffect, useId, useMemo, useRef, useState } from 'react';
import {
  Area,
  AreaChart,
  CartesianGrid,
  Line,
  LineChart,
  ReferenceLine,
  ResponsiveContainer,
  Tooltip as RechartsTooltip,
  XAxis,
  YAxis,
} from 'recharts';
import {
  drawdownYDomain,
  formatDrawdownAxisTick,
  pnlPercentDomain,
} from '../../utils/benchmarkEquitySeries';
import PnlPercentChartTooltip from './PnlPercentChartTooltip';
import { renderTransferMarkers, TRANSFER_MARK_COLOR, transferMarkerPoints } from './transferChartMarkers';

const BENCHMARK_COLORS = { SPY: '#EAB308', QQQ: '#38BDF8' };

const formatRangeDate = (str) => {
  if (!str) return '';
  const d = new Date(`${str}T12:00:00`);
  return d.toLocaleDateString('en-US', { month: '2-digit', day: '2-digit', year: 'numeric' });
};

const formatEquityAxisTick = (v) => {
  if (v == null || !Number.isFinite(Number(v))) return '';
  const n = Number(v);
  const abs = Math.abs(n);
  if (abs >= 1000) return `$${(n / 1000).toFixed(abs >= 10000 ? 0 : 1)}k`;
  return `$${n.toFixed(0)}`;
};

const formatPctTick = (v) => {
  if (!Number.isFinite(Number(v)) || Math.abs(Number(v)) < 0.05) return '0%';
  const n = Number(v);
  return `${n >= 0 ? '+' : ''}${n.toFixed(0)}%`;
};

const formatDate = (str, timeframe) => {
  const d = new Date(`${str}T12:00:00`);
  if (timeframe === 'week' || timeframe === 'today') {
    return d.toLocaleDateString('en-US', { weekday: 'short' });
  }
  if (timeframe === 'year' || timeframe === 'ytd' || timeframe === '2y' || timeframe === '5y' || timeframe === 'all') {
    return d.toLocaleDateString('en-US', { month: 'short', year: '2-digit' });
  }
  return d.toLocaleDateString('en-US', { month: 'short', day: 'numeric' });
};

const formatDrawdownCurrency = (v) =>
  v >= 0 ? `+$${Number(v).toFixed(2)}` : `-$${Math.abs(Number(v)).toFixed(2)}`;

function SeriesKey({ label, color, value, onClick, active = true }) {
  const interactive = typeof onClick === 'function';
  const Tag = interactive ? 'button' : 'span';
  return (
    <Tag
      type={interactive ? 'button' : undefined}
      onClick={onClick}
      style={{
        display: 'inline-flex',
        alignItems: 'center',
        gap: 6,
        minHeight: interactive ? 32 : undefined,
        border: 'none',
        background: 'transparent',
        cursor: interactive ? 'pointer' : 'default',
        opacity: active ? 1 : 0.4,
        padding: interactive ? '4px 2px' : '2px 0',
        color: 'inherit',
        font: 'inherit',
      }}
    >
      <span style={{ width: 10, height: 3, borderRadius: 2, background: color, flexShrink: 0 }} />
      <span style={{ fontSize: '0.75rem', fontWeight: 600 }}>{label}</span>
      {value != null && (
        <span style={{ fontSize: '0.75rem', fontVariantNumeric: 'tabular-nums', fontWeight: 650 }}>
          {value}
        </span>
      )}
    </Tag>
  );
}

/**
 * One question per plot. P&L% is a single percent scale (account vs one index).
 * Account value is a single dollar scale, with drawdown in its own band underneath
 * so the two units never share an axis.
 */
export default function EquityCurvePlot({
  chartData,
  mode = 'pnl',
  colors,
  isMobile = false,
  height = 240,
  timeframe,
  showBenchmark = false,
  benchmarkSymbol = 'SPY',
  benchmarkEndPct = null,
  showDrawdown = true,
  drawdownColor,
  transfers = [],
  isSinglePoint = false,
  legendTrailing = null,
  unavailableNote = null,
  rangeNote = null,
  onActivePointChange = null,
}) {
  const syncId = useId();
  const isPnlMode = mode === 'pnl';
  const benchmarkColor = BENCHMARK_COLORS[benchmarkSymbol] || BENCHMARK_COLORS.SPY;
  const onActiveRef = useRef(onActivePointChange);
  onActiveRef.current = onActivePointChange;
  const dataIdentity = `${chartData[0]?.time ?? ''}:${chartData.length}:${chartData[chartData.length - 1]?.time ?? ''}`;
  const [activePoint, setActivePoint] = useState(null);

  const yPctDomain = useMemo(
    () => pnlPercentDomain(chartData, showBenchmark),
    [chartData, showBenchmark]
  );
  const yDrawdown = useMemo(() => drawdownYDomain(chartData), [chartData]);
  const hasDrawdownSeries = chartData.some((d) => typeof d.drawdown === 'number' && d.drawdown < 0);
  const showDrawdownBand = !isPnlMode && hasDrawdownSeries && showDrawdown;
  const transferPoints = useMemo(
    () => transferMarkerPoints(transfers, chartData),
    [transfers, chartData]
  );
  const axisWidth = isMobile ? 44 : 52;
  // Dollar amounts sit on the right. Percent stays on the left, since that scale is not a balance.
  const plotMargin = isPnlMode
    ? { top: 4, right: 8, left: 0, bottom: 0 }
    : { top: 4, right: 0, left: 8, bottom: 0 };
  const domainHasInteriorZero = isPnlMode
    && typeof yPctDomain[0] === 'number'
    && yPctDomain[0] < -0.05
    && yPctDomain[1] > 0.05;

  useEffect(() => {
    setActivePoint(null);
    onActiveRef.current?.(null);
  }, [dataIdentity]);

  const touchLock = useRef(false);
  const selectPoint = (state) => {
    const label = state?.activeLabel;
    if (label == null) return;
    if (activePoint && String(activePoint.time) === String(label)) return;
    const point = chartData.find((p) => String(p.time) === String(label));
    if (!point) return;
    setActivePoint(point);
    onActiveRef.current?.(point);
  };
  const clearPoint = () => {
    if (touchLock.current) return;
    setActivePoint(null);
    onActiveRef.current?.(null);
  };
  const onTouchMove = (state) => {
    touchLock.current = true;
    selectPoint(state);
  };
  const onTouchEnd = () => {
    touchLock.current = true;
    window.setTimeout(() => {
      touchLock.current = false;
    }, 800);
  };
  const lineDot = isSinglePoint
    ? { r: 5, fill: colors.primary, strokeWidth: 0 }
    : false;
  const tickStyle = { fontSize: isMobile ? 10 : 11, fill: colors.textMuted };
  const dateTick = (value) => formatDate(value, timeframe);

  const formatSignedPct = (v) => {
    if (v == null || !Number.isFinite(Number(v))) return null;
    const n = Number(v);
    return `${n >= 0 ? '+' : ''}${n.toFixed(2)}%`;
  };

  return (
    <div style={{ width: '100%', minWidth: 0, color: colors.textMuted }}>
      {rangeNote ? (
        <div style={{ textAlign: 'right', fontSize: '0.75rem', color: colors.textMuted, marginBottom: 6 }}>
          {rangeNote}
        </div>
      ) : null}
      {unavailableNote ? (
        <div style={{ fontSize: '0.7rem', color: colors.textMuted, margin: '-2px 0 8px' }}>
          {unavailableNote}
        </div>
      ) : null}

      <ResponsiveContainer width="100%" height={height}>
        <LineChart
          data={chartData}
          margin={plotMargin}
          syncId={syncId}
          onMouseMove={selectPoint}
          onTouchMove={onTouchMove}
          onTouchEnd={onTouchEnd}
          onMouseLeave={clearPoint}
        >
          <CartesianGrid stroke={colors.border} strokeOpacity={0.45} vertical={false} />
          <XAxis
            dataKey="time"
            tickFormatter={dateTick}
            hide={showDrawdownBand}
            stroke={colors.border}
            tick={tickStyle}
            tickLine={false}
            axisLine={{ stroke: colors.border }}
            minTickGap={isMobile ? 28 : 40}
          />
          {isPnlMode ? (
            <YAxis
              yAxisId="pct"
              orientation="left"
              width={axisWidth}
              domain={yPctDomain}
              tickFormatter={formatPctTick}
              stroke={colors.border}
              tick={tickStyle}
              tickLine={false}
              axisLine={false}
            />
          ) : (
            <YAxis
              yAxisId="equity"
              orientation="right"
              width={axisWidth}
              domain={['auto', 'auto']}
              tickFormatter={formatEquityAxisTick}
              stroke={colors.border}
              tick={tickStyle}
              tickLine={false}
              axisLine={false}
            />
          )}
          {domainHasInteriorZero && (
            <ReferenceLine
              yAxisId="pct"
              y={0}
              stroke={colors.textMuted}
              strokeOpacity={0.45}
              strokeDasharray="3 3"
            />
          )}
          <RechartsTooltip
            content={
              isPnlMode ? (
                <PnlPercentChartTooltip
                  colors={colors}
                  formatDateLabel={formatRangeDate}
                  showBenchmark={showBenchmark}
                  benchmarkSymbol={benchmarkSymbol}
                  benchmarkColor={benchmarkColor}
                />
              ) : undefined
            }
            contentStyle={
              isPnlMode
                ? undefined
                : {
                    background: colors.bgSecondary,
                    border: `1px solid ${colors.border}`,
                    borderRadius: '8px',
                  }
            }
            labelFormatter={isPnlMode ? undefined : (t) => formatRangeDate(t)}
            formatter={
              isPnlMode
                ? undefined
                : (value, name) => {
                    if (value == null) return ['—', name];
                    if (name === 'equity') {
                      return [
                        `$${Number(value).toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`,
                        'Account value',
                      ];
                    }
                    return [String(value), name];
                  }
            }
          />
          {activePoint && (
            <ReferenceLine
              x={activePoint.time}
              yAxisId={isPnlMode ? 'pct' : 'equity'}
              stroke={colors.text}
              strokeOpacity={0.45}
              strokeWidth={1}
            />
          )}
          {renderTransferMarkers({
            transfers,
            chartData: chartData,
            yAxisId: isPnlMode ? 'pct' : 'equity',
          })}
          {isPnlMode && (
            <>
              <Line
                yAxisId="pct"
                type="monotone"
                dataKey="pnlPct"
                name="pnlPct"
                stroke={colors.primary}
                strokeWidth={2}
                dot={lineDot}
                isAnimationActive={false}
              />
              {showBenchmark && (
                <Line
                  yAxisId="pct"
                  type="monotone"
                  dataKey="benchmarkPct"
                  name="benchmarkPct"
                  stroke={benchmarkColor}
                  strokeWidth={1.75}
                  dot={false}
                  connectNulls
                  isAnimationActive={false}
                />
              )}
            </>
          )}
          {!isPnlMode && (
            <Line
              yAxisId="equity"
              type="monotone"
              dataKey="equity"
              name="equity"
              stroke={colors.primary}
              strokeWidth={2}
              dot={lineDot}
              isAnimationActive={false}
            />
          )}
        </LineChart>
      </ResponsiveContainer>

      {showDrawdownBand && (
        <div style={{ marginTop: 6 }}>
          <div
            style={{
              marginLeft: 8,
              marginBottom: 2,
              fontSize: '0.75rem',
              fontWeight: 600,
              color: drawdownColor || colors.chartDown,
            }}
          >
            Drawdown
          </div>
          <ResponsiveContainer width="100%" height={isMobile ? 72 : 84}>
            <AreaChart data={chartData} margin={plotMargin} syncId={syncId}>
              <XAxis
                dataKey="time"
                tickFormatter={dateTick}
                stroke={colors.border}
                tick={tickStyle}
                tickLine={false}
                axisLine={{ stroke: colors.border }}
                minTickGap={isMobile ? 28 : 40}
              />
              <YAxis
                yAxisId="drawdown"
                orientation="right"
                width={axisWidth}
                domain={yDrawdown}
                tickFormatter={formatDrawdownAxisTick}
                tick={{ fontSize: isMobile ? 10 : 11, fill: drawdownColor || colors.chartDown }}
                tickLine={false}
                axisLine={false}
                tickCount={3}
              />
              <RechartsTooltip
                contentStyle={{
                  background: colors.bgSecondary,
                  border: `1px solid ${colors.border}`,
                  borderRadius: '8px',
                }}
                labelFormatter={(t) => formatRangeDate(t)}
                formatter={(value) => [
                  value == null ? '—' : formatDrawdownCurrency(Number(value)),
                  'Drawdown',
                ]}
              />
              <Area
                yAxisId="drawdown"
                type="monotone"
                dataKey="drawdown"
                name="drawdown"
                stroke={drawdownColor || colors.chartDown}
                fill={drawdownColor || colors.chartDown}
                fillOpacity={0.16}
                strokeWidth={1.5}
                isAnimationActive={false}
              />
            </AreaChart>
          </ResponsiveContainer>
        </div>
      )}

      <div
        style={{
          display: 'flex',
          flexWrap: 'wrap',
          alignItems: 'center',
          justifyContent: 'space-between',
          gap: '4px 12px',
          marginTop: 8,
        }}
      >
        <div style={{ display: 'flex', flexWrap: 'wrap', alignItems: 'center', gap: '2px 14px', minWidth: 0 }}>
          {isPnlMode ? (
            <>
              <SeriesKey label="P&L%" color={colors.primary} />
              {showBenchmark && (
                <SeriesKey
                  label={benchmarkSymbol}
                  color={benchmarkColor}
                  value={formatSignedPct(benchmarkEndPct)}
                />
              )}
            </>
          ) : (
            <SeriesKey label="Account value" color={colors.primary} />
          )}
          {transferPoints.length > 0 && (
            <SeriesKey label="Transfer" color={TRANSFER_MARK_COLOR} />
          )}
        </div>
        {legendTrailing ? (
          <div style={{ display: 'flex', flexWrap: 'wrap', alignItems: 'center', gap: 8, marginLeft: 'auto' }}>
            {legendTrailing}
          </div>
        ) : null}
      </div>
    </div>
  );
}
